import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import whatsappIcon from "../../../img/svg/whatsapp_icon_without_backround.svg";
import { selectPartner, setPartner } from "../../../store/partnerStore";
import { PARTNER_KEYS } from "../../../utils/partner";
import ScreenHeader from "../../components/ScreenHeader";
import "./connectionError.scss";

const WarningIcon = () => (
  <svg className="ce-v2-warning" width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.29 3.86L1.82 18A2 2 0 0 0 3.53 21H20.47A2 2 0 0 0 22.18 18L13.71 3.86A2 2 0 0 0 10.29 3.86Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M12 9V13.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

// V2 connection-error screen (/connection-error) — shown by helpers/swalErrorPopup.js
// when a request fails without a server message (network down, timeout, unexpected).
// Routed outside LayoutV2 (like /partner-error) so silentLogin doesn't re-run under it.
function ConnectionError() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const partner = useSelector(selectPartner);

  // Outside LayoutV2 the partner is never detected, so a hard refresh on this
  // route would fall back to non-partner theming. Rehydrate from localStorage.
  useEffect(() => {
    if (partner !== PARTNER_KEYS.NONE) return;
    const stored = localStorage.getItem("partnerKey");
    if (stored && PARTNER_KEYS[stored]) dispatch(setPartner(stored));
  }, [partner, dispatch]);

  // Back doubles as retry: the previous screen remounts and re-fetches.
  // With no history (failure on first load / deep link), "/" re-runs the
  // handshake from the queryParams cached in localStorage.
  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/", { replace: true });
  };

  return (
    <div className="ce-v2">
      <ScreenHeader title={t("CONNECTION_ERROR_TITLE")} onBack={handleBack} />
      <div className="ce-v2-body">
        <span className="ce-v2-circle ce-v2-circle--top" aria-hidden="true" />
        <span className="ce-v2-circle ce-v2-circle--bottom" aria-hidden="true" />
        <WarningIcon />
        <p className="ce-v2-title">{t("CONNECTION_ERROR_OOPS")}</p>
        <p className="ce-v2-text">{t("CONNECTION_ERROR_TEXT")}</p>
      </div>
      <div className="ce-v2-bottom">
        <button
          type="button"
          className="ce-v2-whatsapp-btn"
          onClick={() => window.open("https://wa.me/972559458809", "_blank")}
        >
          <img src={whatsappIcon} alt="" />
          {t("CONNECTION_ERROR_WHATSAPP_CTA")}
        </button>
      </div>
    </div>
  );
}

export default ConnectionError;
